// Компонент одного предложения упражнения типа keyword_transform.
// Отображает текст с полями для ввода ответов и подсказки (ключевые слова).

import { useState } from 'react';
import { Badge, Button, Modal } from 'react-bootstrap';

// Парсинг шаблона предложения: %N или _____ → массив токенов
function parseTokens(sentence) {
  const tokens = [];
  let remaining = sentence ?? '';
  let autoPos = 0;
  let safety = 0;
  while (remaining.length > 0 && safety++ < 200) {
    const match = remaining.match(/^(.*?)(?:%(\d+)|_{5,})(.*)/s);
    if (!match) {
      tokens.push({ type: 'text', value: remaining });
      break;
    }
    if (match[1]) tokens.push({ type: 'text', value: match[1] });
    const pos = match[2] !== undefined ? match[2] : String(++autoPos);
    tokens.push({ type: 'blank', pos });
    remaining = match[3];
  }
  return tokens;
}

/**
 * KeywordTransformItem — один элемент упражнения словообразования.
 *
 * Props:
 *   sentId:       string          — "1", "2", ...
 *   sentObj:      { sentence: string, words: { [blankPos]: string[] } }
 *   answers:      { [blankPos]: string }  — текущие ответы для этого предложения
 *   onSetAnswer:  (blankPos, value) => void
 *   explanation:  { title: string, description: string } | null
 *   firstInputRef: React.RefObject — ref на первый input (для автофокуса)
 */
export default function KeywordTransformItem({ sentId, sentObj, answers = {}, onSetAnswer, explanation, firstInputRef }) {
  const [showModal, setShowModal] = useState(false);

  const { sentence, words = {}, answer_lengths = {} } = sentObj;
  const blankEntries = Object.entries(words).sort(([a], [b]) => Number(a) - Number(b));

  // Все пропуски заполнены → меняем цвет кружочка
  const allFilled = blankEntries.length > 0 && blankEntries.every(
    ([bp]) => (answers[bp] ?? '').trim() !== ''
  );

  const tokens = parseTokens(sentence);
  // Индекс первого blank-токена для автофокуса
  const firstBlankTokenIndex = tokens.findIndex((t) => t.type === 'blank');

  return (
    <div>
      {/* Номер + текст с полями + кнопка ? */}
      <div className="d-flex align-items-start gap-2 mb-2" style={{ color: '#1e2a4a', fontSize: '0.9375rem', lineHeight: 1.8 }}>

        {/* Кружочек с номером */}
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '1.75rem',
            height: '1.75rem',
            borderRadius: '50%',
            background: allFilled ? 'var(--bs-primary)' : '#e9ecef',
            color: allFilled ? '#fff' : '#6c757d',
            fontSize: '0.75rem',
            fontWeight: 700,
            flexShrink: 0,
            marginTop: '0.25rem',
            transition: 'all 0.15s',
          }}
        >
          {sentId}
        </span>

        {/* Текст предложения с input-полями */}
        <span style={{ fontWeight: 500, flex: 1 }}>
          {tokens.map((tok, i) => {
            if (tok.type === 'text') {
              return <span key={i}>{tok.value}</span>;
            }
            const bp = tok.pos;
            const val = answers[bp] ?? '';
            // Ширина пропуска — по длине правильного ответа (answer_lengths от бэкенда)
            const answerLen = answer_lengths[bp] ?? (words[bp] ?? [])[0]?.length ?? 4;
            const inputWidth = `${(Math.max(answerLen, 4) + 2) * 10}px`;
            const isFirst = i === firstBlankTokenIndex;
            return (
              <input
                key={i}
                ref={isFirst ? firstInputRef : null}
                type="text"
                value={val}
                onChange={(e) => {
                  const v = e.target.value;
                  onSetAnswer(bp, v.trimStart() === '' ? '' : v.trimStart());
                }}
                onBlur={(e) => onSetAnswer(bp, e.target.value.trim())}
                style={{
                  border: 'none',
                  borderBottom: '2px solid var(--bs-primary)',
                  outline: 'none',
                  background: 'transparent',
                  fontSize: 'inherit',
                  fontFamily: 'inherit',
                  lineHeight: 'inherit',
                  padding: 0,
                  color: '#1e2a4a',
                  fontWeight: 600,
                  verticalAlign: 'baseline',
                  textAlign: 'center',
                  width: inputWidth,
                  minWidth: inputWidth,
                  maxWidth: inputWidth,
                }}
              />
            );
          })}
        </span>

        {/* Кнопка ? — только если есть explanation */}
        {explanation && (
          <button
            type="button"
            onClick={() => setShowModal(true)}
            title="Показать правило"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '1.5rem',
              height: '1.5rem',
              borderRadius: '50%',
              border: '1.5px solid #6c757d',
              background: 'transparent',
              color: '#6c757d',
              fontSize: '0.75rem',
              fontWeight: 700,
              cursor: 'pointer',
              flexShrink: 0,
              marginTop: '0.35rem',
              padding: 0,
              lineHeight: 1,
              transition: 'border-color 0.15s, color 0.15s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = '#0dcaf0';
              e.currentTarget.style.color = '#0dcaf0';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = '#6c757d';
              e.currentTarget.style.color = '#6c757d';
            }}
          >
            ?
          </button>
        )}
      </div>

      {/* Keywords — badge-и снизу */}
      {blankEntries.length > 0 && (
        <div className="d-flex align-items-center flex-wrap gap-2" style={{ paddingLeft: '2.25rem' }}>
          {blankEntries.map(([bp, kwArr]) =>
            (kwArr ?? []).map((kw) => (
              <span
                key={`${bp}-${kw}`}
                style={{
                  display: 'inline-block',
                  fontSize: '0.75rem',
                  fontWeight: 700,
                  letterSpacing: '0.04em',
                  color: '#fff',
                  background: '#1e2a4a',
                  borderRadius: '0.35rem',
                  padding: '0.125rem 0.5rem',
                }}
              >
                {kw}
              </span>
            ))
          )}
        </div>
      )}

      {/* Модальное окно с explanation */}
      {explanation && (
        <Modal show={showModal} onHide={() => setShowModal(false)} centered size="sm">
          <Modal.Header closeButton style={{ borderBottom: '1px solid rgba(0,0,0,0.06)', padding: '1.25rem 1.5rem' }}>
            <Modal.Title style={{ fontSize: '1rem', fontWeight: 700 }}>
              {explanation.title ?? 'Правило'}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ padding: '1.5rem' }}>
            <p style={{ lineHeight: 1.7, color: '#1e2a4a', whiteSpace: 'pre-wrap', margin: 0 }}>
              {explanation.description}
            </p>
          </Modal.Body>
          <Modal.Footer style={{ borderTop: '1px solid rgba(0,0,0,0.06)', padding: '1rem 1.5rem' }}>
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => setShowModal(false)}
              style={{ borderRadius: '0.5rem', fontWeight: 500 }}
            >
              Закрыть
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </div>
  );
}
